'use client'

import {
  CSSProperties,
  ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react'
import { X } from 'lucide-react'
import './task-dialog.scss'

type TaskDialogTone = 'neutral' | 'primary' | 'danger' | 'warning'

interface TaskDialogProps {
  isOpen: boolean
  onClose?: () => void
  title: string
  description?: string
  icon?: ReactNode
  tone?: TaskDialogTone
  busy?: boolean
  children?: ReactNode
  footer?: ReactNode
  maxWidth?: number
  bodyClassName?: string
}

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export function TaskDialog({
  isOpen,
  onClose,
  title,
  description,
  icon,
  tone = 'neutral',
  busy = false,
  children,
  footer,
  maxWidth = 440,
  bodyClassName = '',
}: TaskDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  const busyRef = useRef(busy)
  const titleId = useId()
  const descriptionId = useId()

  onCloseRef.current = onClose
  busyRef.current = busy

  useEffect(() => {
    if (!isOpen) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    const scrollY = window.scrollY
    const previousBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      overflow: document.body.style.overflow,
    }

    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.overflow = 'hidden'

    const focusDialog = window.requestAnimationFrame(() => {
      dialogRef.current?.focus()
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onCloseRef.current && !busyRef.current) {
        event.preventDefault()
        onCloseRef.current()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(element => !element.hasAttribute('disabled') && element.offsetParent !== null)

      if (focusable.length === 0) {
        event.preventDefault()
        dialogRef.current.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.cancelAnimationFrame(focusDialog)
      document.removeEventListener('keydown', handleKeyDown)
      Object.assign(document.body.style, previousBodyStyles)
      window.scrollTo(0, scrollY)
      previouslyFocused?.focus()
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className="task-dialog__overlay"
      onMouseDown={event => {
        if (event.target === event.currentTarget && onClose && !busy) onClose()
      }}
    >
      <div
        ref={dialogRef}
        className={`task-dialog task-dialog--${tone}`}
        style={{ '--task-dialog-max-width': `${maxWidth}px` } as CSSProperties}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className="task-dialog__header">
          <div className="task-dialog__heading">
            {icon && <span className="task-dialog__icon" aria-hidden="true">{icon}</span>}
            <div className="task-dialog__heading-copy">
              <h2 id={titleId} className="task-dialog__title">{title}</h2>
              {description && (
                <p id={descriptionId} className="task-dialog__description">{description}</p>
              )}
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              className="task-dialog__close"
              onClick={onClose}
              disabled={busy}
              aria-label="Close dialog"
            >
              <X size={20} strokeWidth={2} />
            </button>
          )}
        </header>

        {children && (
          <div className={`task-dialog__body ${bodyClassName}`.trim()}>{children}</div>
        )}
        {footer && <footer className="task-dialog__footer">{footer}</footer>}
      </div>
    </div>
  )
}
